import type { DraggableAttributes } from '@dnd-kit/core';
import { CSSProperties, KeyboardEvent, memo, Ref, useState } from 'react';

import BriefcaseIcon from '@/assets/svg/ui-elements/briefcase.svg';
import EyeIcon from '@/assets/svg/ui-elements/eye-outline-thin.svg';
import MessageIcon from '@/assets/svg/ui-elements/message-square.svg';
import { theme, Typography } from '@/design-system';
import type {
  PipelineCard as PipelineCardDto,
  PipelineCardProperty,
  PipelineTone,
} from '@/pages/pipelines/common/types';
import {
  getPipelineCardFaceIndicators,
  getPipelineCardPresentation,
} from '@/pages/pipelines/common/utils/card-presentation';

import { pipelineColors } from '../../constants';
import { usePipelineCardAccessory } from './card-accessory-context';
import {
  BadgeRow,
  CardAccessoryRow,
  CardAvatar,
  CardBadge,
  CardButton,
  CardHeader,
  CardMutedText,
  CardTitleGroup,
  CardTitleText,
  IndicatorIcon,
  TextPropertyGroup,
  TimerBadge,
} from './styled';

type TextPropertyRow = {
  kind: 'text-group';
  key: string;
  texts: Extract<PipelineCardProperty, { kind: 'text' }>[];
};
type TagsPropertyRow = {
  kind: 'tags-row';
  property: Extract<PipelineCardProperty, { kind: 'tags' }>;
};
type PropertyRow = TextPropertyRow | TagsPropertyRow;

const toPropertyRows = (properties: PipelineCardProperty[]): PropertyRow[] =>
  properties.reduce<PropertyRow[]>((rows, property) => {
    const lastRow = rows[rows.length - 1];

    if (property.kind === 'text') {
      if (lastRow?.kind === 'text-group') {
        lastRow.texts.push(property);
      } else {
        rows.push({ kind: 'text-group', key: property.key, texts: [property] });
      }
    } else {
      rows.push({ kind: 'tags-row', property });
    }

    return rows;
  }, []);

type Props = {
  card: PipelineCardDto;
  // Optional so the drag overlay can render a non-interactive visual clone.
  onSelect?: (_cardId: string) => void;
  dragHandleProps?: DraggableAttributes & Record<string, unknown>;
  innerRef?: Ref<HTMLDivElement>;
  isDragging?: boolean;
  style?: CSSProperties;
};

const TONE_TO_COLOR: Record<PipelineTone, string> = {
  default: pipelineColors.lightBlue,
  blue: pipelineColors.blue,
  purple: pipelineColors.purple,
  amber: pipelineColors.amber,
  teal: pipelineColors.teal,
  green: pipelineColors.green,
  red: theme.colors.error.solid,
  neutral: pipelineColors.neutralMuted,
};

const INDICATOR_ICONS = {
  eye: EyeIcon,
  message: MessageIcon,
} as const;

const getIndicatorIconKey = (indicator: PipelineCardDto['indicators'][number]) => {
  if (indicator.icon === 'eye' || indicator.icon === 'message') return indicator.icon;

  return null;
};

const AVATAR_TONES: PipelineTone[] = ['blue', 'purple', 'amber', 'teal', 'green'];

const initialsOf = (label: string) =>
  label
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const avatarToneOf = (label: string) => {
  let hash = 0;

  for (const char of label) hash = (hash * 31 + char.charCodeAt(0)) % 997;

  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

export const PipelineCard = memo(
  ({ card, onSelect, dragHandleProps, innerRef, isDragging = false, style }: Props) => {
    const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);
    const renderAccessory = usePipelineCardAccessory();
    const presentation = getPipelineCardPresentation(card);
    const avatar = card.avatar;
    const showPhoto = Boolean(avatar?.url && avatar.url !== failedPhotoUrl);
    const avatarColor = avatar ? TONE_TO_COLOR[avatarToneOf(avatar.label)] : undefined;
    const faceIndicators = getPipelineCardFaceIndicators(card);
    const orderedProperties = card.properties;
    const propertyRows = orderedProperties ? toPropertyRows(orderedProperties) : undefined;
    const accessory = renderAccessory?.(card);
    const accentTone = presentation.accentTone;
    const accentColor = TONE_TO_COLOR[accentTone];
    const indicatorNodes = faceIndicators.map((indicator) => {
      const iconKey = getIndicatorIconKey(indicator);
      const Icon = iconKey ? INDICATOR_ICONS[iconKey] : null;

      return (
        <IndicatorIcon key={indicator.key} aria-label={indicator.label}>
          {Icon ? <Icon /> : indicator.label}
        </IndicatorIcon>
      );
    });

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      // The card now hosts its own focusable controls (the follow-up marker), and their keydown
      // bubbles here. Without this the card swallows Enter on the button and opens the drawer
      // instead. `Table.tsx` guards its rows the same way.
      if (event.target !== event.currentTarget) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        onSelect?.(card.id);
        return;
      }

      if (
        !dragHandleProps &&
        (event.key === ' ' || event.key === 'Space' || event.key === 'Spacebar')
      ) {
        event.preventDefault();
        onSelect?.(card.id);
      }
    };

    return (
      <CardButton
        ref={innerRef}
        {...dragHandleProps}
        role={dragHandleProps?.role ?? 'button'}
        tabIndex={dragHandleProps?.tabIndex ?? 0}
        data-variant={presentation.variant}
        data-accent-tone={accentTone}
        data-outline-tone={card.outlineTone}
        data-title-tone={presentation.titleTone}
        data-has-timer={Boolean(card.timer)}
        data-mixed-tone={presentation.mixedTone}
        data-is-dragging={isDragging}
        style={
          {
            '--accent-color': accentColor,
            ...(card.outlineTone && { '--outline-color': TONE_TO_COLOR[card.outlineTone] }),
            ...style,
          } as CSSProperties
        }
        onClick={() => onSelect?.(card.id)}
        onKeyDown={handleKeyDown}
      >
        <CardHeader>
          {avatar && (
            <CardAvatar
              data-testid="pipeline-card-avatar"
              data-icon={avatar.icon}
              style={{ background: `${avatarColor}33`, color: avatarColor }}
            >
              {showPhoto ? (
                <img src={avatar.url ?? ''} alt="" onError={() => setFailedPhotoUrl(avatar.url)} />
              ) : avatar.icon === 'user' ? (
                initialsOf(avatar.label)
              ) : (
                <BriefcaseIcon />
              )}
            </CardAvatar>
          )}
          <CardTitleGroup data-card-title-group="true">
            <Typography variant="body4" as="div" data-card-title="true">
              <CardTitleText>{presentation.title}</CardTitleText>
            </Typography>
            {!orderedProperties &&
              card.subtitleLines.map((line, index) => (
                <CardMutedText
                  key={`${index}-${line}`}
                  data-tone={line.startsWith('via ') ? 'via' : undefined}
                >
                  {line}
                </CardMutedText>
              ))}
          </CardTitleGroup>
          {card.timer && <TimerBadge data-tone={card.timer.tone}>{card.timer.label}</TimerBadge>}
        </CardHeader>

        {propertyRows ? (
          <>
            {propertyRows.map((row) =>
              row.kind === 'text-group' ? (
                <TextPropertyGroup key={row.key}>
                  {row.texts.map((property) => (
                    <CardMutedText key={property.key}>{property.text}</CardMutedText>
                  ))}
                </TextPropertyGroup>
              ) : (
                <BadgeRow key={row.property.key}>
                  {row.property.tags.map((tag) => (
                    <CardBadge
                      key={tag.key}
                      data-tone={tag.tone ?? 'neutral'}
                      data-variant={tag.variant ?? 'muted'}
                    >
                      {tag.label}
                    </CardBadge>
                  ))}
                </BadgeRow>
              ),
            )}
            {indicatorNodes.length > 0 && <BadgeRow>{indicatorNodes}</BadgeRow>}
          </>
        ) : (
          <BadgeRow>
            {presentation.badges.map((badge) => (
              <CardBadge
                key={badge.key}
                data-tone={badge.tone}
                data-variant={badge.variant ?? 'filled'}
              >
                {badge.label}
              </CardBadge>
            ))}
            {indicatorNodes}
          </BadgeRow>
        )}

        {accessory && <CardAccessoryRow>{accessory}</CardAccessoryRow>}
      </CardButton>
    );
  },
);

PipelineCard.displayName = 'PipelineCard';
